import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Mail, KeyRound, CheckCircle2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { API_URL } from "../../lib/utils";

const inputCls = "bg-white/5 border-white/15 text-white placeholder:text-white/30";

export const BackofficeForgotPassword = ({ initialEmail = "", onBack }) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const detail = (err, fallback) => {
    const d = err.response?.data?.detail;
    return typeof d === "string" ? d : fallback;
  };

  const sendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/backoffice/forgot-password`, { email });
      toast.success(r.data.message);
      setStep(2);
    } catch (err) {
      toast.error(detail(err, "Não foi possível enviar o código"));
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("A nova palavra-passe deve ter pelo menos 8 caracteres"); return; }
    if (password !== confirm) { toast.error("As palavras-passe não coincidem"); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/backoffice/reset-password`, { email, code: code.trim(), new_password: password });
      toast.success(r.data.message);
      setStep(3);
    } catch (err) {
      toast.error(detail(err, "Código inválido ou expirado"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur" data-testid="backoffice-forgot-password">
      {step === 1 && (
        <form onSubmit={sendCode} className="space-y-4">
          <p className="text-sm text-white/60">Enviamos um código de 6 dígitos para o email do dono. O código é válido durante 15 minutos.</p>
          <div className="space-y-1.5">
            <Label htmlFor="fp-email" className="text-white/70">Email do dono</Label>
            <Input id="fp-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="email do dono" data-testid="forgot-email-input" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold" data-testid="forgot-send-code-btn">
            <Mail className="w-4 h-4 mr-2" /> {loading ? "A enviar..." : "Enviar código"}
          </Button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={resetPassword} className="space-y-4">
          <p className="text-sm text-white/60">Introduza o código recebido em <span className="text-white">{email}</span> e escolha a nova palavra-passe.</p>
          <div className="space-y-1.5">
            <Label htmlFor="fp-code" className="text-white/70">Código de 6 dígitos</Label>
            <Input id="fp-code" inputMode="numeric" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className={`${inputCls} h-12 text-center text-2xl font-mono tracking-[0.5em]`} placeholder="000000" data-testid="forgot-code-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fp-password" className="text-white/70">Nova palavra-passe (mín. 8 caracteres)</Label>
            <Input id="fp-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="forgot-new-password-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fp-confirm" className="text-white/70">Confirmar palavra-passe</Label>
            <Input id="fp-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} data-testid="forgot-confirm-password-input" />
          </div>
          <Button type="submit" disabled={loading || code.length !== 6} className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold" data-testid="forgot-reset-btn">
            <KeyRound className="w-4 h-4 mr-2" /> {loading ? "A repor..." : "Repor palavra-passe"}
          </Button>
          <button type="button" onClick={() => setStep(1)} className="w-full text-xs text-white/40 hover:text-white/70" data-testid="forgot-resend-link">Não recebeu? Pedir novo código</button>
        </form>
      )}

      {step === 3 && (
        <div className="text-center space-y-3" data-testid="forgot-success">
          <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />
          <p className="text-sm text-white/70">Palavra-passe alterada. Todas as sessões anteriores foram terminadas.</p>
          <Button onClick={onBack} className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold" data-testid="forgot-done-btn">Entrar com a nova palavra-passe</Button>
        </div>
      )}

      {step !== 3 && (
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-white/40 hover:text-white/70" data-testid="forgot-back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Voltar ao login
        </button>
      )}
    </div>
  );
};
